import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider/';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import { setRefundAssistStatus } from './api';
import messages from './messages';
import style from './style.module.less';
import { PromotionalBanner } from '../../../components/promotional-banner';
import { SimpleModal } from '../../../components/simple-modal';
import { Segment } from '../../../services/analytics-services/segment';
import { REFUND_ASSIST_ENABLE_TOGGLE } from '../../../constants/analytics-events';
import { REFUND_ASSIST_TOGGLE_SOURCE } from '../../../constants/refund-assist-source-constants';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { useIsRefundAssistEnabled } from '../../../hooks/use-seller-setting';
import { LocaleContext } from '../../../intl/locale-context-provider';
import { getLink } from '../../../services/link-service';
import { linkIDs } from '../../../services/link-service/links';
import { LogService } from '../../../services/log-service';
import Global from '../../../store/global-selectors';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

export const RefundAssistSettings = () => {
  const { locale } = useContext(LocaleContext);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const dispatch = useDispatch();
  const intl = useIntl();
  const isRefundAssistEnabled = useIsRefundAssistEnabled();
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useAsyncSafeState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useAsyncSafeState(false);

  const performToggleUpdate = useCallback(
    (newValue) => {
      setIsLoading(true);
      setRefundAssistStatus(newValue)
        .then(() => {
          const successMessage = newValue ? messages.successfullyEnabledRefundAssist : messages.successfullyDisabledRefundAssist;

          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: intl.formatMessage(successMessage),
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
          Segment.trackEvent(REFUND_ASSIST_ENABLE_TOGGLE, {
            isEnabled: newValue,
            source: REFUND_ASSIST_TOGGLE_SOURCE.SETTINGS,
          });
          const refundAssistConfig = {
            [SellerSettings.isRefundAssistEnabled.key]: newValue,
          };
          dispatch(saveNewSellerSettings(refundAssistConfig));
        })
        .catch((error) => {
          LogService.logError(new Error('Failed to update refund assist status', { cause: error }), {
            newValue,
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            data: error?.response?.data,
            message: error?.message,
          });
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.failedToSave} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, intl, setIsLoading],
  );

  const handleRefundAssistToggle = useCallback(() => {
    if (isLoading) return;

    const newValue = !isRefundAssistEnabled;

    // If enabling, proceed directly
    if (newValue) {
      performToggleUpdate(newValue);
    } else {
      // User is disabling - always show modal for manual toggle interaction
      setIsDisableModalOpen(true);
    }
  }, [isRefundAssistEnabled, performToggleUpdate, setIsDisableModalOpen, isLoading]);

  const handleConfirmDisable = useCallback(() => {
    setIsDisableModalOpen(false);
    performToggleUpdate(false);
  }, [performToggleUpdate, setIsDisableModalOpen]);

  const handleCancelDisable = useCallback(() => {
    setIsDisableModalOpen(false);
  }, [setIsDisableModalOpen]);

  const handleDisclaimerToggle = useCallback(() => {
    setIsDisclaimerExpanded((prev) => !prev);
  }, [setIsDisclaimerExpanded]);

  const kbLink = getLink(linkIDs.RefundAssist, homeCountry, locale);

  return (
    <FlexColumn className={style['refund-assist-page-content']}>
      <SettingsPageTitle value={messages.refundAssistTitle} />
      <Text textType={TextType.Description} value={messages.refundAssistDescription} />
      <FlexColumn className={classnames(style['refund-assist-section'], style['header-section'])}>
        <Button className={style.learnMoreLink} href={kbLink} rel='noreferrer' target='_blank' theme='link'>
          <Text textType={TextType.Paragraph} value={messages.learnMoreAboutRefundAssist} />
          <FontAwesomeIcon className={style.learnMoreIcon} icon={faArrowUpRightFromSquare} />
        </Button>
      </FlexColumn>
      {!isRefundAssistEnabled && (
        <FlexColumn className={classnames(style['refund-assist-section'], style['header-section'])}>
          <PromotionalBanner
            promotionalHeaderMessage={messages.upgradeBannerTitle}
            promotionalSubheaderMessage={messages.upgradeBannerMessage}
          />
        </FlexColumn>
      )}
      <Card className={classnames(style['refund-assist-card'])}>
        <CheckboxSlider
          checkboxId='refund-assist-toggle'
          checked={isRefundAssistEnabled}
          className={style['refund-assist-toggle']}
          disabled={isLoading}
          labelText={
            <Text
              className={style['refund-assist-toggle-label']}
              textType={TextType.Description}
              value={messages.enableRefundAssistLabel}
            />
          }
          onChange={handleRefundAssistToggle}
        />

        {isRefundAssistEnabled ? (
          <>
            <FlexColumn className={style['disclaimer-content']}>
              <Text textType={TextType.Label} value={messages.disclaimerAuthorization} />
              {isDisclaimerExpanded ? (
                <>
                  <Text textType={TextType.Paragraph} value={messages.disclaimerReview} />
                  <Text textType={TextType.Paragraph} value={messages.disclaimerSubmission} />
                  <Text textType={TextType.Paragraph} value={messages.disclaimerViewCredit} />
                </>
              ) : null}
              <Button className={style['disclaimer-toggle-button']} onClick={handleDisclaimerToggle} theme='link'>
                <Text
                  className={style['disclaimer-toggle-text']}
                  textType={TextType.Label}
                  value={isDisclaimerExpanded ? messages.readLess : messages.readMore}
                />
                <FontAwesomeIcon className={style['disclaimer-arrow']} icon={isDisclaimerExpanded ? faChevronUp : faChevronDown} />
              </Button>
            </FlexColumn>

            <div className={style['usps-disclaimer-card']}>
              <Text textType={TextType.Label} value={messages.uspsDisclaimerTitle} />
              <Text textType={TextType.Caption} value={messages.uspsDisclaimerContentParagraph1} />
              <Text textType={TextType.Caption} value={messages.uspsDisclaimerContentParagraph2} />
            </div>
          </>
        ) : null}
      </Card>
      {isDisableModalOpen ? (
        <SimpleModal
          actionMessage={messages.disableModalConfirm}
          cancelMessage={messages.disableModalCancel}
          headerMessage={messages.disableModalTitle}
          onAction={handleConfirmDisable}
          onCancel={handleCancelDisable}
          onClose={handleCancelDisable}
          size='medium'
        >
          <Text textType={TextType.Label} value={messages.disableModalContentHeading} />
          <FlexRow className={style['disable-refund-assist-modal-bullet-points']}>
            <li>
              <Text element='span' textType={TextType.Paragraph} value={messages.disableModalContentParagraph1} />
            </li>
            <li>
              <Text element='span' textType={TextType.Paragraph} value={messages.disableModalContentParagraph2} />
            </li>
          </FlexRow>
          <Text textType={TextType.Label} value={messages.disableModalContentFooter} />
        </SimpleModal>
      ) : null}
    </FlexColumn>
  );
};
